import { useCallback, useMemo } from "react"
import { useInventions } from "../inventions/InventionsProvider";
import { useTrademarks } from "../trademarks/TrademarksProvider";
import { Family } from "../patents/patents";
import Fuse from "fuse.js";

import { family_member, patent_family, trade_mark, trademark_family } from "../data";
import { useRoles } from "../user/Auth";
import { usePatents } from "../patents/PatentsProvider";
import { useProducts } from "../products/ProductsProvider";
import { useTasks } from "../tasks/TasksProvider";
import { augment } from "../tasks/utils";
import { useUserSettings } from "../user/UserSettingsProvider";
import { useTags } from "../tags/TagsProvider";

const defaultFuseConfig = {
    includeScore: true,
    ignoreLocation: true,
    threshold: 0.3,
    includeMatches: true,
    useExtendedSearch: true,
}

export function useIpSearch() {
    const {hasBrands, hasInnovation, hasTasks} = useRoles()
    // What should we do with active /inactive IP rights?
    const { tagsLookup } = useTags()
    const { commodities, commodityById } = useProducts()
    const { inventions } = useInventions()
    const { trademarks, trademarkFamilies, trademarkById } = useTrademarks()
    const { members, families: _families, familyById, memberById } = usePatents()
    const { todos } = useTasks()
    const {users} = useUserSettings()

    const families = _families.map((f: Family) => ({...f, tags: tagsLookup[patent_family]?.[f.patentFamilyId] ?? []}))

    const familyFuse = useMemo(() => new Fuse(families, { 
        ...defaultFuseConfig,
        keys: ['internalReference', 'familyName', 'summary', 'externalReference', 'tags'],
        
    }), [families])

    const memberFuse = useMemo(() => new Fuse(members, {
        ...defaultFuseConfig,
        keys: ['internalReference', 'externalReference', 'title', 'countryCode', 'applicationNumber', 'publicationNumber', 'patentNumber']
    }), [members])

    const productFuse = useMemo(() => new Fuse(commodities, {
        ...defaultFuseConfig,
        keys: ['commodityReference', 'commodityClass', 'commodityDescription'] 
    }), [commodities])

    const inventionFuse = useMemo(() => new Fuse(inventions, {
        ...defaultFuseConfig,
        keys: ['reference', 'title', 'summary'] 
    }), [inventions])

    const trademarkFuse = useMemo(() => new Fuse(trademarks, {
        ...defaultFuseConfig,
        keys: ['reference', 'words', 'applicationNumber', 'registrationNumber', 'countryCode'],
    }), [trademarks])

    const trademarkFamilyFuse = useMemo(() => new Fuse(trademarkFamilies, {
        ...defaultFuseConfig,
        keys: ['reference', 'name', 'description'],
    }), [trademarkFamilies])

    const taskFuse = useMemo(() => new Fuse(todos.map(t => ({...t, ...augment(t, familyById, memberById, trademarkById, users, commodityById)})), {
        ...defaultFuseConfig,
        keys: ['title', 'comment', 'dueDate', 'internalDueDate' ],
    }), [todos, familyById, memberById, trademarkById, users, commodityById])

    const search = useCallback(
        (searchTerm: string) => {

            const fuse_matched_families = familyFuse.search(searchTerm, { limit: 15 })
            const fuse_matched_members = memberFuse.search(searchTerm, { limit: 15 })
            const fuse_matched_products = productFuse.search(searchTerm, { limit: 15 })
            const fuse_matched_inventions = hasInnovation ? inventionFuse.search(searchTerm, { limit: 15 }) : []
            const fuse_matched_trademarks = hasBrands ? trademarkFuse.search(searchTerm, { limit: 15 }) : []
            const fuse_matched_trademark_families = hasBrands ? trademarkFamilyFuse.search(searchTerm, { limit: 15 }) : []
            const fuse_matched_tasks = hasTasks ? taskFuse.search(searchTerm, { limit: 15 }) : []
            //console.log({fuse_matched_tasks})

            return {
                families: fuse_matched_families.map(i => ({ ...i, type: patent_family })),
                members: fuse_matched_members.map(i => ({ ...i, type: family_member })),
                commodities: fuse_matched_products.map(i => ({ ...i, type: 'commodity' })),
                inventions: fuse_matched_inventions.map(i => ({ ...i, type: 'invention' })),
                trademarks: fuse_matched_trademarks.map(i => ({ ...i, type: trade_mark })),
                trademarkFamilies: fuse_matched_trademark_families.map(i => ({ ...i, type: trademark_family })),
                tasks: fuse_matched_tasks.map(i => ({ ...i, type: 'task' }))
            }
        }, [
        //families, members, commodities, inventions, trademarks, trademarkFamilies,
        hasInnovation, hasBrands, hasTasks,
        familyFuse, memberFuse, inventionFuse, productFuse, trademarkFamilyFuse, trademarkFuse, taskFuse,
    ])

    return {search}
}